import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useContacts } from '../../context/ContactsContext';

export const useUCPListPage = () => {
  const {
    contacts,
    contactsMeta,
    loadingContacts,
    error,
    isEmpty,
    selectedContact,
    setSelectedContact,
    fetchContacts,
    fetchContactDetails
  } = useContacts();

  const [searchTerm, setSearchTermState] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [filters, setFiltersState] = useState({});

  const searchTimeoutRef = useRef(null);
  const cityFilter = filters.city ?? null;

  useEffect(() => {
    console.log('searchTerm############################', searchTerm);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchContacts({
        page: currentPage,
        page_size: itemsPerPage,
        search: searchTerm ? searchTerm  : null,
        filters: Object.keys(filters).length > 0 ? filters : null
      });
    }, searchTerm ? 500 : 0);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [currentPage, itemsPerPage, searchTerm, filters, fetchContacts]);

  const meta = useMemo(() => {
    if (!contactsMeta) return null;
    
    return {
      totalItems: contactsMeta.totalItems,
      totalPages: contactsMeta.totalPages,
      currentPage: contactsMeta.currentPage,
      perPage: contactsMeta.perPage,
      hasNext: contactsMeta.hasNext,
      hasPrevious: contactsMeta.hasPrevious
    };
  }, [contactsMeta]);

  const enhancedContacts = useMemo(() => {
    return contacts.map(contact => ({
      ...contact,
      city: contact.address?.addressCity || '',
      addressLine: contact.address?.addressLine || '',
      state: contact.address?.addressState || '',
      postalCode: contact.address?.addressPostalCode || '',
      uuid: contact.partyUuid,
    }));
  }, [contacts]);

  const handleSelectContact = async (contactUuid) => {
    if (selectedContact?.partyUuid === contactUuid) return;

    await fetchContactDetails(contactUuid);
  };

  const refreshContacts = useCallback(() => {
    fetchContacts({
      page: currentPage,
      page_size: itemsPerPage,
      search: searchTerm ? searchTerm  : null,
      filters: Object.keys(filters).length > 0 ? filters : null
    });
  }, [currentPage, itemsPerPage, searchTerm, filters, fetchContacts]);

  const setSearchTerm = useCallback((value) => {
    setCurrentPage(1);
    setSearchTermState(value);
  }, []);

  const setCityFilter = useCallback((value) => {
    setCurrentPage(1);
    setFiltersState((prev) => {
      if (!value) {
        if (!prev.city) {
          return prev;
        }
        const { city, ...rest } = prev;
        return rest;
      }

      if (prev.city === value && Object.keys(prev).length === 1) {
        return prev;
      }

      return { ...prev, city: value };
    });
  }, []);

  const resetSearch = () => {
    setCurrentPage(1);
    setSearchTermState('');
    setFiltersState({});
  };

  return {
    contacts: enhancedContacts,
    meta,
    loading: loadingContacts,
    error,
    isEmpty,
    selectedContact,
    
    // Local UI state
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    filters,
    cityFilter,
    setCityFilter,
    
    // Actions
    setSelectedContact,
    handleSelectContact,
    refreshContacts,
    resetSearch,
    fetchContactDetails
  };
};

